import * as q from "../../queries.ts";
import { formatError, type RpcClient } from "../api.ts";
import { PagedList } from "../components/paged-list.tsx";
import { PageHeader } from "../components/page-header.tsx";
import { ActorLink, PostCard } from "../components/post.tsx";
import { isFiltered } from "../filters.ts";
import { resolveDid } from "../view-cache.ts";
import type { PostListKind } from "../router.ts";

const titles: Record<PostListKind, string> = {
  likes: "Liked by",
  reposts: "Reposted by",
  quotes: "Quotes",
};

export function PostListView(
  client: RpcClient,
  actor: string,
  rkey: string,
  kind: PostListKind,
  signal: AbortSignal,
): Element {
  const main = (
    <main class="post-list-page">
      <p class="status">Loading…</p>
    </main>
  );
  void resolveList(client, actor, rkey, kind, main, signal);
  return main;
}

async function resolveList(
  client: RpcClient,
  actor: string,
  rkey: string,
  kind: PostListKind,
  target: Element,
  signal: AbortSignal,
): Promise<void> {
  let uri: `at://${string}`;
  try {
    const did = await resolveDid(client, actor, signal);
    uri = `at://${did}/blue.cerulea.app.post/${rkey}` as const;
  } catch (error) {
    if (!signal.aborted) {
      target.replaceChildren(
        PageHeader({ title: titles[kind] }),
        <div class="error" role="alert">
          <strong>Could not load this post</strong>
          <span>{formatError(error)}</span>
        </div>,
      );
    }
    return;
  }
  if (signal.aborted) return;
  const parameters = (cursor?: string) => ({ uri, limit: 50, ...(cursor && { cursor }) });
  const list = kind === "quotes"
    ? PagedList(
      async (cursor) => {
        const page = await client.call(q.getPostQuotes, { parameters: parameters(cursor), signal });
        return { items: page.posts, ...(page.cursor && { cursor: page.cursor }) };
      },
      (post) => post.uri,
      (post) => isFiltered(post) ? undefined : PostCard({ post, signal }),
      signal,
      "No quotes yet.",
    )
    : PagedList(
      async (cursor) => {
        const rpc = kind === "likes" ? q.getPostLikes : q.getPostReposts;
        const page = await client.call(rpc, { parameters: parameters(cursor), signal });
        return { items: page.actors, ...(page.cursor && { cursor: page.cursor }) };
      },
      (actor) => actor.did,
      (actor) => <div class="actor-row">{ActorLink({ actor })}</div>,
      signal,
      `No ${kind} yet.`,
    );
  if (signal.aborted) return;
  target.replaceChildren(PageHeader({ title: titles[kind] }), list);
}
